// ===================================================================
// 🎓 GERENCIADOR DE CURSOS - IMPLEMENTAÇÃO ESPECÍFICA
// ===================================================================

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { styles } from '../../styles/globalStyles';
import { Alert } from '../../components/ui/Alert';
import { Modal } from '../../components/ui/Modal';
import { LoadingSpinner } from '../../components/ui/LoadingSpinner';
import { apiClient } from '../../api/apiClient';
import { validateFields } from '../../utils/helpers';
import { useNotifications } from '../../contexts/NotificationContext';
import { sortColumnIndicator } from '../../utils/adminTableSort';
import { requiresCurricularSubjectsMatrix, isIntentionLetterMode, isOrdemInscricaoMode, isProvaMode } from '../../utils/selectionMode';
import { charLimitsFromLines } from '../../utils/intentionLetter';

const normalizeCityStateLabel = (value) => {
  const txt = String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[\-/,;]+/g, ' ')
    .replace(/[^a-zA-Z0-9 ]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .toUpperCase();
  return txt;
};

const isCityStateLabelValid = (value) => /^[A-Z0-9 ]+ [A-Z]{2}$/.test(String(value || ''));

const initialCourseSort = { key: null, dir: 'asc' };

const nextCourseSort = (prev, key) => {
  if (prev.key !== key) return { key, dir: 'asc' };
  return { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' };
};

export const CourseManager = React.memo(({ user }) => {
  const [courses, setCourses] = useState([]);
  const [processes, setProcesses] = useState([]);
  const [quotas, setQuotas] = useState([]);
  const [courseTypes, setCourseTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingCourse, setEditingCourse] = useState(null);
  const [formData, setFormData] = useState({
    process_id: '',
    name: '',
    code: '',
    description: '',
    campus: '',
    total_vacancies: 0,
    course_type: 'integrado',
    modality: 'presencial',
    quota_vacancies: [],
    subjects: [],
    exam_area_weights: [],
    letter_min_lines: 1,
    letter_max_lines: 10,
    letter_rubric: [{ id: 'c1', label: '', max_points: 10 }],
    letter_instructions: '',
  });
  const [error, setError] = useState('');
  const [tableSort, setTableSort] = useState(initialCourseSort);
  const { addNotification } = useNotifications();
  const processNameById = useMemo(() => {
    const map = new Map();
    processes.forEach((p) => map.set(p.id, p.name || ''));
    return map;
  }, [processes]);

  const sortedCourses = useMemo(() => {
    const list = [...courses];
    if (!tableSort.key) return list;

    const dir = tableSort.dir === 'asc' ? 1 : -1;
    list.sort((a, b) => {
      if (tableSort.key === 'process') {
        const aProcess = processNameById.get(a.process_id) || '';
        const bProcess = processNameById.get(b.process_id) || '';
        const byProcess = aProcess.localeCompare(bProcess, 'pt-BR');
        if (byProcess !== 0) return byProcess * dir;
        return String(a.name || '').localeCompare(String(b.name || ''), 'pt-BR') * dir;
      }
      const byName = String(a.name || '').localeCompare(String(b.name || ''), 'pt-BR');
      if (byName !== 0) return byName * dir;
      const aProcess = processNameById.get(a.process_id) || '';
      const bProcess = processNameById.get(b.process_id) || '';
      return aProcess.localeCompare(bProcess, 'pt-BR') * dir;
    });
    return list;
  }, [courses, processNameById, tableSort]);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [coursesData, processesData, quotasData, courseTypesData] = await Promise.all([
        apiClient.getCourses(),
        apiClient.getProcesses(true),
        apiClient.getQuotas(),
        apiClient.getCourseTypes(),
      ]);
      setCourses(Array.isArray(coursesData) ? coursesData : []);
      setProcesses(Array.isArray(processesData) ? processesData : []);
      setQuotas(Array.isArray(quotasData) ? quotasData : []);
      setCourseTypes(Array.isArray(courseTypesData) ? courseTypesData : []);
    } catch (err) {
      setError('Erro ao carregar cursos');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const defaultCourseTypeCode = useMemo(
    () => (courseTypes.length > 0 ? courseTypes[0].code : 'integrado'),
    [courseTypes],
  );

  const courseTypeLabelByCode = useMemo(() => {
    const map = {};
    courseTypes.forEach((t) => {
      map[t.code] = t.name;
    });
    return map;
  }, [courseTypes]);

  const selectedProcess = processes.find(p => p.id === parseInt(formData.process_id, 10));
  const useQuotas = selectedProcess?.use_quotas === true;
  const isProvaProcess = isProvaMode(selectedProcess?.selection_mode);
  const isOrdemProcess = isOrdemInscricaoMode(selectedProcess?.selection_mode);
  const showSubjectsMatrix = requiresCurricularSubjectsMatrix(selectedProcess?.selection_mode);
  const showIntentionLetterConfig = isIntentionLetterMode(selectedProcess?.selection_mode);
  const letterLimitsPreview = charLimitsFromLines(formData.letter_min_lines, formData.letter_max_lines);

  useEffect(() => {
    if (useQuotas && quotas.length && formData.process_id && !editingCourse) {
      setFormData(prev => ({
        ...prev,
        quota_vacancies: quotas.map(q => ({
          quota_id: q.id,
          vacancies: prev.quota_vacancies?.find(pq => pq.quota_id === q.id)?.vacancies ?? 0
        }))
      }));
    }
  }, [formData.process_id, useQuotas, quotas.length, editingCourse]);

  const handleInputChange = useCallback((field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (error) setError('');
  }, [error]);

  const calculateTotalFromQuotaVacancies = useCallback((qv) => {
    if (!Array.isArray(qv)) return 0;
    return qv.reduce((total, x) => total + (parseInt(x.vacancies, 10) || 0), 0);
  }, []);

  const handleQuotaVacancyChange = useCallback((quotaId, vacancies) => {
    setFormData(prev => {
      const qv = [...(prev.quota_vacancies || [])];
      const idx = qv.findIndex(x => x.quota_id === quotaId);
      if (idx >= 0) {
        qv[idx] = { ...qv[idx], vacancies: parseInt(vacancies, 10) || 0 };
      } else {
        qv.push({ quota_id: quotaId, vacancies: parseInt(vacancies, 10) || 0 });
      }
      const total = qv.reduce((s, x) => s + (parseInt(x.vacancies, 10) || 0), 0);
      return { ...prev, quota_vacancies: qv, total_vacancies: total };
    });
  }, []);

  const handleSubjectChange = useCallback((index, field, value) => {
    setFormData(prev => {
      const newSubjects = [...(prev.subjects || [])];
      newSubjects[index] = { ...newSubjects[index], [field]: value };
      return { ...prev, subjects: newSubjects };
    });
  }, []);

  const addSubject = useCallback(() => {
    setFormData(prev => ({
      ...prev,
      subjects: [...(prev.subjects || []), { 
        serie: '7º ano', 
        disciplinas: [{ name: '', weight: 1 }] 
      }]
    }));
  }, []);

  const removeSubject = useCallback((index) => {
    setFormData(prev => ({
      ...prev,
      subjects: prev.subjects.filter((_, i) => i !== index)
    }));
  }, []);

  const addDisciplina = useCallback((serieIndex) => {
    setFormData(prev => {
      const newSubjects = [...(prev.subjects || [])];
      // Verificar se já existe uma disciplina vazia na última posição
      const lastDisciplina = newSubjects[serieIndex].disciplinas[newSubjects[serieIndex].disciplinas.length - 1];
      if (lastDisciplina && lastDisciplina.name === '' && lastDisciplina.weight === 1) {
        // Se já existe uma disciplina vazia, não adicionar outra
        return prev;
      }
      newSubjects[serieIndex].disciplinas.push({ name: '', weight: 1 });
      return { ...prev, subjects: newSubjects };
    });
  }, []);

  const removeDisciplina = useCallback((serieIndex, disciplinaIndex) => {
    setFormData(prev => {
      const newSubjects = [...(prev.subjects || [])];
      newSubjects[serieIndex].disciplinas.splice(disciplinaIndex, 1);
      return { ...prev, subjects: newSubjects };
    });
  }, []);

  const handleDisciplinaChange = useCallback((serieIndex, disciplinaIndex, field, value) => {
    setFormData(prev => {
      const newSubjects = [...(prev.subjects || [])];
      newSubjects[serieIndex].disciplinas[disciplinaIndex] = { 
        ...newSubjects[serieIndex].disciplinas[disciplinaIndex], 
        [field]: value 
      };
      return { ...prev, subjects: newSubjects };
    });
  }, []);

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    
    if (!validateFields({ process_id: formData.process_id, name: formData.name, code: formData.code, campus: formData.campus })) {
      setError('Preencha processo, nome, código e unidade do curso');
      return;
    }
    const totalVac = useQuotas
      ? calculateTotalFromQuotaVacancies(formData.quota_vacancies)
      : (formData.total_vacancies || 0);
    if (totalVac <= 0) {
      setError(useQuotas ? 'Configure pelo menos uma cota com vagas' : 'Informe o total de vagas');
      return;
    }

    const normalizedCampus = normalizeCityStateLabel(formData.campus);
    if (!isCityStateLabelValid(normalizedCampus)) {
      setError('Unidade deve estar no formato CIDADE UF (ex.: PIRAPORA MG)');
      return;
    }
    if (!courseTypes.length) {
      setError('Cadastre ao menos um tipo de curso em Configuração → Tipos de curso');
      return;
    }

    try {
      setLoading(true);
      
      const apiPayload = {
        process_id: parseInt(formData.process_id, 10),
        name: (formData.name || '').trim(),
        code: (formData.code || '').trim() || null,
        description: (formData.description || '').trim(),
        campus: normalizedCampus || null,
        course_type: formData.course_type || defaultCourseTypeCode,
        modality: formData.modality || 'presencial',
        total_vacancies: totalVac
      };
      if (useQuotas && formData.quota_vacancies?.length) {
        apiPayload.quota_vacancies = formData.quota_vacancies.filter(q => (q.vacancies || 0) > 0);
      }
      if (showSubjectsMatrix) {
        apiPayload.subjects = Array.isArray(formData.subjects) ? formData.subjects : [];
      } else {
        apiPayload.subjects = [];
      }
      if (showIntentionLetterConfig) {
        const rubric = (formData.letter_rubric || []).filter((r) => (r.label || '').trim());
        if (!rubric.length) {
          setError('Cadastre ao menos um critério no barema da carta de intenção');
          setLoading(false);
          return;
        }
        apiPayload.letter_min_lines = parseInt(formData.letter_min_lines, 10) || 0;
        apiPayload.letter_max_lines = parseInt(formData.letter_max_lines, 10) || 0;
        apiPayload.letter_rubric = rubric.map((r, i) => ({
          id: r.id || `c${i + 1}`,
          label: (r.label || '').trim(),
          max_points: parseFloat(r.max_points) || 0,
        }));
        apiPayload.letter_instructions = (formData.letter_instructions || '').trim();
      }
      if (editingCourse) {
        const { error: updateError } = await apiClient.updateCourse(editingCourse.id, apiPayload);
        if (updateError) {
          setError(updateError);
        } else {
          addNotification('Sucesso', 'Curso atualizado com sucesso', 'success');
          setShowModal(false);
          setEditingCourse(null);
          loadData();
        }
      } else {
        const { error: createError } = await apiClient.createCourse(apiPayload);
        if (createError) {
          setError(createError);
        } else {
          addNotification('Sucesso', 'Curso criado com sucesso', 'success');
          setShowModal(false);
          loadData();
        }
      }
    } catch (err) {
      setError('Erro ao salvar curso');
    } finally {
      setLoading(false);
    }
  }, [formData, editingCourse, loadData, addNotification, useQuotas, calculateTotalFromQuotaVacancies, selectedProcess, showSubjectsMatrix, showIntentionLetterConfig, courseTypes, defaultCourseTypeCode]);

  const handleEdit = useCallback((course) => {
    setEditingCourse(course);
    const proc = processes.find(p => p.id === course.process_id);
    const useQ = proc?.use_quotas === true;
    let qv = course.quota_vacancies || [];
    if (useQ && quotas.length && (!qv.length || !qv.some(x => x.quota_id))) {
      qv = quotas.map(q => ({
        quota_id: q.id,
        vacancies: (course.quota_vacancies?.find(cq => cq.quota_id === q.id)?.vacancies) || 0
      }));
    }
    setFormData({
      process_id: course.process_id || '',
      name: course.name || '',
      code: course.code || '',
      description: course.description || '',
      campus: course.campus || '',
      total_vacancies: course.total_vacancies ?? course.vacancies ?? 0,
      course_type: course.course_type || 'integrado',
      modality: course.modality || 'presencial',
      quota_vacancies: qv,
      subjects: course.subjects || [],
      exam_area_weights: [],
      letter_min_lines: course.letter_min_lines ?? 1,
      letter_max_lines: course.letter_max_lines ?? 10,
      letter_rubric: (course.letter_rubric && course.letter_rubric.length)
        ? course.letter_rubric
        : [{ id: 'c1', label: '', max_points: 10 }],
      letter_instructions: course.letter_instructions || '',
    });
    setShowModal(true);
  }, [processes, quotas]);

  const handleDelete = useCallback(async (courseId) => {
    if (!window.confirm('Tem certeza que deseja excluir este curso?')) return;
    try {
      setLoading(true);
      const { error: deleteError } = await apiClient.deleteCourse(courseId);
      if (deleteError) {
        setError(deleteError);
        addNotification('Erro', deleteError, 'error');
      } else {
        addNotification('Sucesso', 'Curso excluído com sucesso', 'success');
        loadData();
      }
    } catch (err) {
      setError('Erro ao excluir curso');
    } finally {
      setLoading(false);
    }
  }, [loadData, addNotification]);

  const handleNewCourse = useCallback(() => {
    setEditingCourse(null);
    setFormData({
      process_id: '',
      name: '',
      code: '',
      description: '',
      campus: '',
      total_vacancies: 0,
      course_type: defaultCourseTypeCode,
      modality: 'presencial',
      quota_vacancies: [],
      subjects: [],
      exam_area_weights: [],
      letter_min_lines: 1,
      letter_max_lines: 10,
      letter_rubric: [{ id: 'c1', label: '', max_points: 10 }],
      letter_instructions: '',
    });
    setShowModal(true);
  }, [defaultCourseTypeCode]);

  const getCourseTypeLabel = useCallback((type) => {
    if (courseTypeLabelByCode[type]) return courseTypeLabelByCode[type];
    const legacy = {
      integrado: 'Integrado',
      concomitante_subsequente: 'Concomitante/Subsequente',
      concomitante: 'Concomitante',
      subsequente: 'Subsequente',
      fic: 'FIC',
    };
    return legacy[type] || type || '—';
  }, [courseTypeLabelByCode]);

  const getModalityLabel = useCallback((modality) => {
    const modalities = {
      presencial: 'Presencial',
      ead: 'EAD'
    };
    return modalities[modality] || modality;
  }, []);

  const getQuotaTypeLabel = useCallback((type) => {
    const QUOTA_TYPES = {
      AC: "Ampla Concorrência",
      PCD: "Pessoa com Deficiência",
      EFA: "Ação Afirmativa IFNMG",
      LB_PPI: "Baixa Renda - PPI",
      LB_EP: "Baixa Renda - Escola Pública",
      LB_PCD: "Baixa Renda - PCD",
      LB_Q: "Baixa Renda - Quilombola",
      LI_PPI: "Renda Independente - PPI",
      LI_EP: "Renda Independente - Escola Pública",
      LI_PCD: "Renda Independente - PCD",
      LI_Q: "Renda Independente - Quilombola"
    };
    return QUOTA_TYPES[type] || type;
  }, []);

  const getQuotaTypeShort = useCallback((type) => {
    return type || 'AC';
  }, []);

  if (loading && courses.length === 0) {
    return <LoadingSpinner text="Carregando cursos..." />;
  }

  return (
    <div style={styles.contentContainer}>
      <div style={styles.card}>
        <div style={styles.cardHeader}>
          <h2 style={styles.cardTitle}>🎓 Gerenciamento de Cursos</h2>
          <button
            onClick={handleNewCourse}
            style={{
              ...styles.button,
              ...styles.buttonPrimary
            }}
          >
            ➕ Novo Curso
          </button>
        </div>

        {error && !showModal && (
          <Alert 
            message={error} 
            type="error" 
            onClose={() => setError('')} 
          />
        )}

        {courses.length === 0 ? (
          <div style={{
            textAlign: 'center',
            padding: '48px 24px',
            color: '#6B7280',
            backgroundColor: '#F9FAFB',
            borderRadius: '12px',
            border: '2px dashed #E5E7EB'
          }}>
            <div style={{ fontSize: '56px', marginBottom: '20px' }}>🎓</div>
            <h3 style={{ margin: '0 0 8px 0', fontSize: '18px', color: '#374151' }}>Nenhum curso cadastrado</h3>
            <p style={{ margin: '0 0 24px 0', fontSize: '14px' }}>Clique em "Novo Curso" para criar o primeiro curso.</p>
            <button
              onClick={handleNewCourse}
              style={{ ...styles.button, ...styles.buttonPrimary }}
            >
              ➕ Novo Curso
            </button>
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={styles.table}>
              <thead style={styles.tableHeader}>
                <tr>
                  <th
                    role="columnheader"
                    aria-sort={tableSort.key === 'process' ? (tableSort.dir === 'desc' ? 'descending' : 'ascending') : 'none'}
                    style={{ ...styles.tableCompactHeaderCell, cursor: 'pointer', userSelect: 'none' }}
                    onClick={() => setTableSort((prev) => nextCourseSort(prev, 'process'))}
                    title="Ordenar por processo"
                  >
                    Processo{sortColumnIndicator(tableSort, 'process')}
                  </th>
                  <th
                    role="columnheader"
                    aria-sort={tableSort.key === 'course_name' ? (tableSort.dir === 'desc' ? 'descending' : 'ascending') : 'none'}
                    style={{ ...styles.tableCompactHeaderCell, cursor: 'pointer', userSelect: 'none' }}
                    onClick={() => setTableSort((prev) => nextCourseSort(prev, 'course_name'))}
                    title="Ordenar por nome do curso"
                  >
                    Nome do Curso{sortColumnIndicator(tableSort, 'course_name')}
                  </th>
                  <th style={styles.tableCompactHeaderCell}>Código</th>
                  <th style={styles.tableCompactHeaderCell}>Unidade</th>
                  <th style={styles.tableCompactHeaderCell}>Vagas</th>
                  <th style={styles.tableCompactHeaderCell}>Tipo</th>
                  <th style={styles.tableCompactHeaderCell}>Modalidade</th>
                  <th style={styles.tableCompactHeaderCell}>Ações</th>
                </tr>
              </thead>
              <tbody>
                {sortedCourses.map(course => {
                  const process = processes.find(p => p.id === course.process_id);
                  return (
                    <tr key={course.id} style={styles.tableRow}>
                      <td style={styles.tableCompactCell}>
                        <div style={{ fontWeight: '600', color: '#1F2937', fontSize: '12px' }}>
                          {process?.name}
                        </div>
                        <div style={{ fontSize: '11px', color: '#6B7280' }}>
                          {process?.code}
                        </div>
                      </td>
                      <td style={styles.tableCompactCell}>
                        <div style={{ fontWeight: '600', color: '#1F2937', fontSize: '12px' }}>
                          {course.name}
                        </div>
                      </td>
                      <td style={styles.tableCompactCell}>
                        <span style={{ 
                          fontFamily: 'monospace', 
                          backgroundColor: '#F3F4F6', 
                          padding: '2px 6px', 
                          borderRadius: '4px',
                          fontSize: '11px'
                        }}>
                          {course.code}
                        </span>
                      </td>
                      <td style={styles.tableCompactCell}>
                        {course.campus}
                      </td>
                      <td style={styles.tableCompactCell}>
                        <span style={{ color: '#10B981', fontWeight: '600', fontSize: '12px' }}>
                          {course.total_vacancies}
                        </span>
                      </td>
                      <td style={styles.tableCompactCell}>
                        <span style={{
                          ...styles.statusBadge,
                          fontSize: '11px',
                          padding: '2px 6px',
                          ...(course.course_type === 'integrado' ? styles.statusApproved : 
                               course.course_type === 'concomitante' ? styles.statusPending : 
                               styles.statusWarning)
                        }}>
                          {getCourseTypeLabel(course.course_type)}
                        </span>
                      </td>
                      <td style={styles.tableCompactCell}>
                        <span style={{
                          ...styles.statusBadge,
                          fontSize: '11px',
                          padding: '2px 6px',
                          ...(course.modality === 'presencial' ? styles.statusApproved : 
                               styles.statusPending)
                        }}>
                          {getModalityLabel(course.modality)}
                        </span>
                      </td>

                      <td style={{ ...styles.tableCompactCell, minWidth: '100px' }}>
                        <div style={{ display: 'flex', gap: '4px', justifyContent: 'center' }}>
                          <button
                            onClick={() => handleEdit(course)}
                            style={{
                              ...styles.button,
                              ...styles.buttonSecondary,
                              ...styles.actionBtnIcon
                            }}
                            title="Editar"
                          >
                            <span>✏️</span>
                            <span style={{ fontSize: '9px', lineHeight: 1 }}>Editar</span>
                          </button>
                          <button
                            onClick={() => handleDelete(course.id)}
                            style={{
                              ...styles.button,
                              ...styles.buttonDanger,
                              ...styles.actionBtnIcon
                            }}
                            title="Excluir"
                          >
                            <span>🗑️</span>
                            <span style={{ fontSize: '9px', lineHeight: 1 }}>Excluir</span>
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Modal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        title={editingCourse ? 'Editar Curso' : 'Novo Curso'}
        large
      >
        {error && (
          <div style={{ marginBottom: '16px' }}>
            <Alert
              message={error}
              type="error"
              onClose={() => setError('')}
            />
          </div>
        )}
        <form onSubmit={handleSubmit} style={styles.form}>
          {/* Informações Básicas */}
          <div style={{ marginBottom: '24px', padding: '16px', backgroundColor: '#F9FAFB', borderRadius: '8px' }}>
            <h4 style={{ margin: '0 0 16px 0', fontSize: '16px', color: '#374151' }}>Informações Básicas</h4>
            
            <div style={styles.formRow}>
              <div style={styles.formGroup}>
                <label style={styles.label}>Processo Seletivo *</label>
                <select
                  value={formData.process_id}
                  onChange={(e) => handleInputChange('process_id', parseInt(e.target.value))}
                  style={styles.select}
                  required
                >
                  <option value="">Selecione um processo...</option>
                  {processes.map(process => (
                    <option key={process.id} value={process.id}>
                      {process.name} ({process.code})
                      {process.selection_mode === 'prova' ? ' — prova' : ''}
                    </option>
                  ))}
                </select>
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Nome do Curso *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  style={styles.input}
                  placeholder="Ex: Técnico em Informática"
                  required
                />
              </div>
            </div>

            <div style={styles.formRow}>
              <div style={styles.formGroup}>
                <label style={styles.label}>Código *</label>
                <input
                  type="text"
                  value={formData.code}
                  onChange={(e) => handleInputChange('code', e.target.value)}
                  style={styles.input}
                  placeholder="Ex: TINF"
                  required
                />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Unidade *</label>
                <input
                  type="text"
                  value={formData.campus}
                  onChange={(e) => handleInputChange('campus', e.target.value)}
                  onBlur={(e) => handleInputChange('campus', normalizeCityStateLabel(e.target.value))}
                  style={styles.input}
                  placeholder="Ex: PIRAPORA MG"
                  required
                />
              </div>
            </div>

            <div style={styles.formRow}>
              <div style={styles.formGroup}>
                <label style={styles.label}>Tipo do Curso *</label>
                {courseTypes.length === 0 ? (
                  <div style={{ fontSize: '13px', color: '#B45309', padding: '10px 12px', background: '#FFFBEB', borderRadius: 8, border: '1px solid #FCD34D' }}>
                    Nenhum tipo de curso ativo. Cadastre em <strong>Configuração → Tipos de curso</strong>.
                  </div>
                ) : (
                  <select
                    value={formData.course_type}
                    onChange={(e) => handleInputChange('course_type', e.target.value)}
                    style={styles.select}
                    required
                  >
                    {courseTypes.map((t) => (
                      <option key={t.id} value={t.code}>{t.name}</option>
                    ))}
                  </select>
                )}
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Modalidade *</label>
                <select
                  value={formData.modality}
                  onChange={(e) => handleInputChange('modality', e.target.value)}
                  style={styles.select}
                  required
                >
                  <option value="presencial">Presencial</option>
                  <option value="ead">EAD</option>
                </select>
              </div>
            </div>

            <div style={styles.formRow}>
              <div style={styles.formGroup}>
                <label style={styles.label}>Descrição</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => handleInputChange('description', e.target.value)}
                  style={styles.textarea}
                  placeholder="Descrição do curso..."
                  rows="3"
                />
              </div>
            </div>
          </div>

          {useQuotas ? (
            <div style={{ marginBottom: '24px', padding: '16px', backgroundColor: '#FEF3C7', borderRadius: '8px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                <h4 style={{ margin: 0, fontSize: '16px', color: '#92400E' }}>Sistema de Cotas</h4>
                <span style={{ fontSize: '14px', color: '#92400E', fontWeight: '600' }}>
                  Total de Vagas: {formData.total_vacancies || 0}
                </span>
              </div>
              {quotas.length === 0 ? (
                <div style={{ padding: '16px', backgroundColor: 'white', borderRadius: '6px', border: '1px solid #E5E7EB' }}>
                  <p style={{ margin: 0, color: '#6B7280' }}>Cadastre cotas no menu &quot;Cotas&quot; para definir vagas por cota.</p>
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  {quotas.map(q => {
                    const qv = formData.quota_vacancies?.find(x => x.quota_id === q.id);
                    const vac = qv?.vacancies ?? 0;
                    return (
                      <div key={q.id} style={{ display: 'flex', alignItems: 'center', gap: '16px', padding: '12px', backgroundColor: 'white', borderRadius: '6px', border: '1px solid #E5E7EB' }}>
                        <div style={{ flex: '0 0 80px', fontWeight: '600', color: '#374151' }}>{q.code}</div>
                        <div style={{ flex: 1, color: '#6B7280', fontSize: '14px' }}>{q.description}</div>
                        <div style={{ flex: '0 0 120px' }}>
                          <label style={styles.label}>Vagas</label>
                          <input
                            type="number"
                            min="0"
                            value={vac || ''}
                            onChange={(e) => handleQuotaVacancyChange(q.id, e.target.value.replace(/[^0-9]/g, '') || 0)}
                            onWheel={(e) => e.preventDefault()}
                            style={{ ...styles.input, appearance: 'none', WebkitAppearance: 'none' }}
                            placeholder="0"
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          ) : (
            <div style={{ marginBottom: '24px', padding: '16px', backgroundColor: '#F3F4F6', borderRadius: '8px' }}>
              <h4 style={{ margin: '0 0 12px 0', fontSize: '16px', color: '#374151' }}>Total de Vagas</h4>
              <div style={styles.formGroup}>
                <label style={styles.label}>Número de vagas *</label>
                <input
                  type="number"
                  min="1"
                  value={formData.total_vacancies || ''}
                  onChange={(e) => handleInputChange('total_vacancies', parseInt(e.target.value, 10) || 0)}
                  onWheel={(e) => e.preventDefault()}
                  style={styles.input}
                  placeholder="Ex: 40"
                />
              </div>
            </div>
          )}

          {isProvaProcess && (
            <div style={{ marginBottom: '24px', padding: '14px 16px', backgroundColor: '#ECFDF5', borderRadius: '8px', border: '1px solid #6EE7B7' }}>
              <p style={{ margin: 0, fontSize: '14px', color: '#065F46', lineHeight: 1.55 }}>
                <strong>Processo por prova:</strong> disciplinas/áreas, quantidade de questões e pesos da prova objetiva são cadastrados em{' '}
                <strong>Processos</strong> (botão <strong>Gabarito</strong>), por processo — não por curso.
              </p>
            </div>
          )}

          {isOrdemProcess && (
            <div style={{ marginBottom: '24px', padding: '14px 16px', backgroundColor: '#ECFDF5', borderRadius: '8px', border: '1px solid #6EE7B7' }}>
              <p style={{ margin: 0, fontSize: '14px', color: '#065F46', lineHeight: 1.55 }}>
                <strong>Por ordem de inscrição:</strong> a classificação segue a ordem cronológica da inscrição entre candidatos deferidos.
                Não é necessário cadastrar disciplinas nem pesos neste curso.
              </p>
            </div>
          )}

          {showIntentionLetterConfig && (
            <div style={{ marginBottom: '24px', padding: '16px', backgroundColor: '#FEF3C7', borderRadius: '8px', border: '1px solid #FCD34D' }}>
              <h4 style={{ margin: '0 0 12px 0', fontSize: '16px', color: '#92400E' }}>Carta de intenção</h4>
              <p style={{ fontSize: '12px', color: '#78350F', margin: '0 0 12px 0' }}>
                80 caracteres por linha (Roboto Mono 12px). Limite de caracteres calculado automaticamente.
                Preview: {formData.letter_min_lines}–{formData.letter_max_lines} linhas ({letterLimitsPreview.minCharacters}–{letterLimitsPreview.maxCharacters} caracteres).
              </p>
              <div style={styles.formRow}>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Mínimo de linhas</label>
                  <input
                    type="number"
                    min={0}
                    value={formData.letter_min_lines ?? 0}
                    onChange={(e) => handleInputChange('letter_min_lines', parseInt(e.target.value, 10) || 0)}
                    style={styles.input}
                  />
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Máximo de linhas</label>
                  <input
                    type="number"
                    min={1}
                    value={formData.letter_max_lines ?? 10}
                    onChange={(e) => handleInputChange('letter_max_lines', parseInt(e.target.value, 10) || 0)}
                    style={styles.input}
                  />
                </div>
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Instruções ao candidato (opcional)</label>
                <textarea
                  value={formData.letter_instructions || ''}
                  onChange={(e) => handleInputChange('letter_instructions', e.target.value)}
                  rows={3}
                  style={{ ...styles.input, width: '100%', resize: 'vertical' }}
                />
              </div>
              <h5 style={{ margin: '16px 0 8px', fontSize: '14px' }}>Barema (critérios e pontuação)</h5>
              {(formData.letter_rubric || []).map((row, idx) => (
                <div key={row.id || idx} style={{ ...styles.formRow, marginBottom: '8px', alignItems: 'flex-end' }}>
                  <div style={{ ...styles.formGroup, flex: 2 }}>
                    <label style={styles.label}>Critério</label>
                    <input
                      type="text"
                      value={row.label || ''}
                      onChange={(e) => {
                        const next = [...(formData.letter_rubric || [])];
                        next[idx] = { ...next[idx], label: e.target.value };
                        handleInputChange('letter_rubric', next);
                      }}
                      style={styles.input}
                      placeholder="Ex.: Clareza e organização das ideias"
                    />
                  </div>
                  <div style={{ ...styles.formGroup, flex: 1 }}>
                    <label style={styles.label}>Pontos máx.</label>
                    <input
                      type="number"
                      min={0.01}
                      step="0.01"
                      value={row.max_points ?? ''}
                      onChange={(e) => {
                        const next = [...(formData.letter_rubric || [])];
                        next[idx] = { ...next[idx], max_points: parseFloat(e.target.value) || 0 };
                        handleInputChange('letter_rubric', next);
                      }}
                      style={styles.input}
                    />
                  </div>
                  <button
                    type="button"
                    onClick={() => handleInputChange('letter_rubric', (formData.letter_rubric || []).filter((_, i) => i !== idx))}
                    style={{ ...styles.button, ...styles.buttonDanger, ...styles.buttonSmall, marginBottom: '4px' }}
                  >
                    Remover
                  </button>
                </div>
              ))}
              <button
                type="button"
                onClick={() => handleInputChange('letter_rubric', [
                  ...(formData.letter_rubric || []),
                  { id: `c${Date.now()}`, label: '', max_points: 10 },
                ])}
                style={{ ...styles.button, ...styles.buttonSecondary, ...styles.buttonSmall, marginTop: '8px' }}
              >
                ➕ Adicionar critério
              </button>
            </div>
          )}

          {/* Sistema de Disciplinas — apenas análise curricular */}
          {showSubjectsMatrix && (
          <div style={{ marginBottom: '24px', padding: '16px', backgroundColor: '#E0E7FF', borderRadius: '8px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <h4 style={{ margin: 0, fontSize: '16px', color: '#3730A3' }}>Sistema de Disciplinas e Pesos</h4>
              <button
                type="button"
                onClick={addSubject}
                style={{
                  ...styles.button,
                  ...styles.buttonSecondary,
                  ...styles.buttonSmall
                }}
              >
                ➕ Adicionar Série
              </button>
            </div>

            {formData.subjects?.map((serie, serieIndex) => (
              <div key={serieIndex} style={{ 
                marginBottom: '16px', 
                padding: '16px', 
                backgroundColor: 'white', 
                borderRadius: '8px',
                border: '1px solid #E5E7EB'
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <span style={{ fontWeight: '600', color: '#374151', fontSize: '16px' }}>
                      {serie.serie}
                    </span>
                    <select
                      value={serie.serie}
                      onChange={(e) => handleSubjectChange(serieIndex, 'serie', e.target.value)}
                      style={{
                        ...styles.select,
                        width: '120px',
                        fontSize: '12px'
                      }}
                    >
                      <option value="6º ano">6º ano</option>
                      <option value="7º ano">7º ano</option>
                      <option value="8º ano">8º ano</option>
                      <option value="9º ano">9º ano</option>
                      <option value="1º ano">1º ano</option>
                      <option value="2º ano">2º ano</option>
                      <option value="3º ano">3º ano</option>
                    </select>
                  </div>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <button
                      type="button"
                      onClick={() => addDisciplina(serieIndex)}
                      style={{
                        ...styles.button,
                        ...styles.buttonSecondary,
                        ...styles.buttonSmall
                      }}
                    >
                      ➕ Disciplina
                    </button>
                    <button
                      type="button"
                      onClick={() => removeSubject(serieIndex)}
                      style={{
                        ...styles.button,
                        ...styles.buttonDanger,
                        ...styles.buttonSmall
                      }}
                    >
                      🗑️ Série
                    </button>
                  </div>
                </div>
                
                {serie.disciplinas?.map((disciplina, disciplinaIndex) => (
                  <div key={disciplinaIndex} style={{ 
                    marginBottom: '8px', 
                    padding: '8px', 
                    backgroundColor: '#F9FAFB', 
                    borderRadius: '4px',
                    border: '1px solid #E5E7EB'
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                      <span style={{ fontSize: '12px', color: '#6B7280' }}>
                        Disciplina {disciplinaIndex + 1}
                      </span>
                      <button
                        type="button"
                        onClick={() => removeDisciplina(serieIndex, disciplinaIndex)}
                        style={{
                          ...styles.button,
                          ...styles.buttonDanger,
                          ...styles.buttonSmall,
                          padding: '2px 6px',
                          fontSize: '10px'
                        }}
                      >
                        🗑️
                      </button>
                    </div>
                    <div style={styles.formRow}>
                      <div style={styles.formGroup}>
                        <label style={{ ...styles.label, fontSize: '12px' }}>Nome da Disciplina</label>
                        <input
                          type="text"
                          value={disciplina.name || ''}
                          onChange={(e) => handleDisciplinaChange(serieIndex, disciplinaIndex, 'name', e.target.value)}
                          style={{ ...styles.input, fontSize: '12px' }}
                          placeholder="Ex: Matemática"
                        />
                      </div>
                      <div style={styles.formGroup}>
                        <label style={{ ...styles.label, fontSize: '12px' }}>Peso</label>
                        <input
                          type="text"
                          value={disciplina.weight || ''}
                          onChange={(e) => {
                            const value = e.target.value.replace(/[^0-9.]/g, '');
                            // Permitir apenas um ponto decimal
                            const parts = value.split('.');
                            const cleanValue = parts.length > 2 ? parts[0] + '.' + parts.slice(1).join('') : value;
                            // Limitar a 2 casas decimais
                            const finalValue = cleanValue.includes('.') ? 
                              cleanValue.split('.')[0] + '.' + cleanValue.split('.')[1].substring(0, 2) : 
                              cleanValue;
                            handleDisciplinaChange(serieIndex, disciplinaIndex, 'weight', parseFloat(finalValue) || 0);
                          }}
                          onWheel={(e) => e.preventDefault()}
                          style={{ 
                            ...styles.input, 
                            fontSize: '12px',
                            appearance: 'none',
                            WebkitAppearance: 'none',
                            MozAppearance: 'textfield'
                          }}
                          placeholder="Ex: 1.5"
                        />
                      </div>
                    </div>
                  </div>
                ))}
                
                {(!serie.disciplinas || serie.disciplinas.length === 0) && (
                  <div style={{ textAlign: 'center', padding: '12px', color: '#6B7280', fontSize: '12px' }}>
                    <p>Nenhuma disciplina configurada para esta série</p>
                    <p>Clique em "➕ Disciplina" para adicionar</p>
                  </div>
                )}
              </div>
            ))}

            {(!formData.subjects || formData.subjects.length === 0) && (
              <div style={{ textAlign: 'center', padding: '20px', color: '#6B7280' }}>
                <p>Nenhuma disciplina configurada</p>
                <p style={{ fontSize: '12px' }}>Adicione disciplinas para análise das notas</p>
              </div>
            )}
          </div>
          )}

          <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', marginTop: '24px' }}>
            <button
              type="button"
              onClick={() => setShowModal(false)}
              style={{
                ...styles.button,
                ...styles.buttonSecondary
              }}
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={loading}
              style={{
                ...styles.button,
                ...styles.buttonPrimary,
                opacity: loading ? 0.7 : 1
              }}
            >
              {loading ? 'Salvando...' : (editingCourse ? 'Atualizar' : 'Criar')}
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
});